import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { PlatformService } from '@core/platform';
import { WINDOW } from '@innogy/utils-dom';

type VisitorInstance = {
  getMarketingCloudVisitorID(): string;
};

type VisitorJS = {
  getInstance: (orgId: string) => VisitorInstance;
};

declare global {
  interface Window {
    Visitor?: VisitorJS;
  }
}

const ANONYMOUS_USER_TOKEN = 'ANONYMOUS_USER';

@Injectable({
  providedIn: 'root',
})
export class VisitorIdentificationService {
  readonly #window = inject(WINDOW);
  readonly #environmentConfig = inject(ENVIRONMENT_CONFIG);
  readonly #platform = inject(PlatformService);
  readonly #orgId = '0E57467352783B530A490D45@AdobeOrg';
  private readonly maxAttempts = 50;
  private readonly interval = 100;

  // Cache containing a reference to the Visitor.js instance.
  #instance?: VisitorInstance;

  // Resolved visitorID for synchronous access.
  #resolvedVisitorId?: string;

  /**
   * Determines whether or not to check if we can resolve a token via Visitor.js
   * @returns boolean indicating whether or not Visitor.js should be checked.
   */
  #shouldCheckForVisitor() {
    const { enabled, url } = this.#environmentConfig.adobeLaunch ?? {};
    return !!(this.#platform.isClient() && enabled && url);
  }

  /**
   * Checks if Adobe Visitor.js is loaded and returns a Promise
   * @param maxAttempts Maximum number of attempts to check for Visitor.js
   * @param interval Time between attempts in milliseconds
   * @returns Promise that resolves with a boolean indicating whether Visitor.js is loaded, after at most maxAttempts
   */
  async #isLoaded(
    maxAttempts = this.maxAttempts,
    interval = this.interval
  ): Promise<boolean> {
    return new Promise((resolve) => {
      let attempts = 0;

      const checkVisitor = () => {
        attempts++;

        // Check if Visitor.js is available
        if (
          this.#window.Visitor?.getInstance &&
          typeof this.#window.Visitor?.getInstance(
            this.#orgId
          ).getMarketingCloudVisitorID() === 'string'
        ) {
          resolve(true);
        } else if (attempts < maxAttempts) {
          setTimeout(checkVisitor, interval);
        } else {
          resolve(false);
        }
      };

      checkVisitor();
    });
  }

  /**
   * Provides a reference to the Visitor.js instance.
   * @returns A promise resolving to the Adobe Web SDK instance.
   */
  private async visitor() {
    if (!this.#instance) {
      const visitorResolved = await this.#isLoaded();
      // Resolves null when visitor cannot be resolved.
      if (!visitorResolved) {
        return null;
      }
      this.#instance = this.#window.Visitor?.getInstance(this.#orgId);
    }
    return this.#instance;
  }

  /**
   * Promise that returns a token identifying the user
   * @returns a token identifiying the user
   */
  async resolveVisitorID() {
    if (!this.#resolvedVisitorId) {
      // If we are running outside the scope where the Visitor.js is loadable
      // We return a random ID
      if (!this.#shouldCheckForVisitor()) {
        this.#resolvedVisitorId = `local-userid-${(Math.random() + 1).toString(36).substring(7)}`;
      } else {
        // Visitor.js should be available.
        // Resolve the visitorId if not in cache yet.
        const visitorInstance = await this.visitor();

        // Resolve with an anonymous token if the VisitorJS instance is not loaded.
        if (!visitorInstance) {
          return ANONYMOUS_USER_TOKEN;
        }

        const id = visitorInstance?.getMarketingCloudVisitorID();
        this.#resolvedVisitorId = id;
      }
    }
    return this.#resolvedVisitorId ?? ANONYMOUS_USER_TOKEN;
  }
}
